import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";

import { DemoInstanceWatermark } from "./components/ui/DemoInstanceWatermark";
import { AppProvider } from "./providers/app";
import { AppRoutes } from "./routes";

import "./index.css";

const contentsquareId = import.meta.env.VITE_CONTENTSQUARE_ID;
const heapEnvId = import.meta.env.VITE_HEAP_ENV_ID;
const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN;

if (import.meta.env.VITE_APP_ENV === "dev") {
  if (contentsquareId) {
    const script = document.createElement("script");
    script.src = `https://t.contentsquare.net/uxa/${contentsquareId}.js`;
    script.defer = true;
    document.head.appendChild(script);
  }

  if (heapEnvId) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.textContent = `window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","identifyHashed","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};heap.load("${heapEnvId}");`;
    document.head.appendChild(script);
  }

  if (mixpanelToken) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.textContent = `(function(e,c){if(!c.__SV){var l,h;window.mixpanel=c;c._i=[];c.init=function(q,r,f){function t(d,a){var g=a.split(".");2==g.length&&(d=d[g[0]],a=g[1]);d[a]=function(){d.push([a].concat(Array.prototype.slice.call(arguments,0)))}}var b=c;"undefined"!==typeof f?b=c[f]=[]:f="mixpanel";b.people=b.people||[];b.toString=function(d){var a="mixpanel";"mixpanel"!==f&&(a+="."+f);d||(a+=" (stub)");return a};b.people.toString=function(){return b.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders start_session_recording stop_session_recording people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");for(h=0;h<l.length;h++)t(b,l[h]);var n="set set_once union unset remove delete".split(" ");b.get_group=function(){function d(p){a[p]=function(){b.push([g,[p].concat(Array.prototype.slice.call(arguments,0))])}}for(var a={},g=["get_group"].concat(Array.prototype.slice.call(arguments,0)),m=0;m<n.length;m++)d(n[m]);return a};c._i.push([q,r,f])};c.__SV=1.2;var k=e.createElement("script");k.type="text/javascript";k.async=!0;k.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"===e.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";e=e.getElementsByTagName("script")[0];e.parentNode.insertBefore(k,e)}})(document,window.mixpanel||[]);mixpanel.init("${mixpanelToken}",{autocapture:{click:true,pageview:"full-url",capture_extra_attrs:["data-heap"]},record_sessions_percent:100});`;
    document.head.appendChild(script);
  }
}

const demoInstance = Cookies.get("demo_instance") || null;

const environment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT === "local"
    ? "local"
    : import.meta.env.VITE_APP_ENV;

Sentry.init({
  dsn: "https://2c573868408733d3d7c2ebfa81979cb1@o4509413111496704.ingest.us.sentry.io/4509413125980160",
  environment,
  enabled: environment !== "local",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    // TODO (https://chorusview.atlassian.net/browse/SWE-1847): enable when we can handle the CORS error on the API side
    // "localhost",
    // /^https:\/\/internal-dev\.chorussystems\.net/,
    // /^https:\/\/internal\.chorussystems\.net/
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <DemoInstanceWatermark demoInstance={demoInstance} />
    <BrowserRouter>
      <AppProvider>
        <AppRoutes />
      </AppProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
